import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import RequireRole from './RequireRole';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 100%;
  max-height: 85vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  
  &:hover {
    color: #34495e;
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0.25rem 0 0.5rem 0;
  font-size: 1.05rem;
  color: #2c3e50;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 220px);
  gap: 0.5rem;
  justify-content: center;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
`;

const SummaryCol = styled.div`
  flex: 0 1 720px;
    min-width: 280px;
  min-height: 0;
`;
const Card = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem;
`;

const CardTitle = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
`;

const CardValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #3498db;
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #ced4da;
  resize: vertical;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  background: #f1f3f5;
  text-align: left;
  padding: 0.6rem;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #dee2e6;
`;

const Td = styled.td`
  padding: 0.6rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
  font-size: 0.92rem;
`;

const RowChanged = styled.tr`
  background: #fcf8e3; /* suave para indicar cambio */
`;

const DiffBadge = styled.span`
  display: inline-block;
  background: ${props => props.$changed ? '#ffc107' : '#e9ecef'};
  color: ${props => props.$changed ? '#7f4f00' : '#6c757d'};
  padding: 0.15rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

// Resúmenes compactos
const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${props => props.$changed ? '#fff0cc' : '#eef2f7'};
  color: ${props => props.$changed ? '#7f4f00' : '#2c3e50'};
  border: 1px solid ${props => props.$changed ? '#ffd27f' : '#dee2e6'};
  font-size: 0.8rem;
  line-height: 1.2;
`;

const SubHeader = styled.div`
  margin-top: 4px;
  margin-bottom: 2px;
  font-size: 0.8rem;
  color: #495057;
`;

// Cantidad al lado del key (más grande y en negrita)
const Qty = styled.span`
  font-weight: 700;
  font-size: calc(1em + 2px);
`;

const Footer = styled.div`
  padding: 0.75rem 1rem 1rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Left = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Right = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.6rem 1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.95rem;
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.disabled ? '#6c757d' : '#28a745'};
  color: white;
  border-color: ${props => props.disabled ? '#6c757d' : '#28a745'};
`;

const SecondaryButton = styled(Button)`
  background: #f8f9fa;
  color: #2c3e50;
  border-color: #dee2e6;
`;

const DangerButton = styled(Button)`
  background: #fff5f5;
  color: #dc3545;
  border-color: #f5c2c7;
`;

const selectStyles = {
  container: (provided) => ({
    ...provided,
    minWidth: '150px',
  }),
  control: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1100,
  })
};

const DIAS = [
  { value: 'LUNES', label: 'Lunes' },
  { value: 'MARTES', label: 'Martes' },
  { value: 'MIERCOLES', label: 'Miércoles' },
  { value: 'JUEVES', label: 'Jueves' },
  { value: 'VIERNES', label: 'Viernes' },
  { value: 'SABADO', label: 'Sábado' },
  { value: 'DOMINGO', label: 'Domingo' },
];

const FRECUENCIAS = [
  { value: 'SEMANAL', label: 'Semanal' },
  { value: 'QUINCENAL', label: 'Quincenal' },
  { value: 'MENSUAL', label: 'Mensual' },
];

// Orden fijo para UI
const ORDER_FRECS = ['SEMANAL', 'QUINCENAL', 'MENSUAL'];
const ORDER_DIAS = DIAS.map(d => d.value);
const ORDER_SEMANAS = ['SEMANA 1', 'SEMANA 2', 'SEMANA 3', 'SEMANA 4', 'SEMANA 1-3', 'SEMANA 2-4'];

function semanaOptionsByFrecuencia(freq) {
  if (freq === 'QUINCENAL') {
    return [
      { value: 'SEMANA 1-3', label: 'Semana 1-3' },
      { value: 'SEMANA 2-4', label: 'Semana 2-4' },
    ];
  }
  if (freq === 'MENSUAL') {
    return [
      { value: 'SEMANA 1', label: 'Semana 1' },
      { value: 'SEMANA 2', label: 'Semana 2' },
      { value: 'SEMANA 3', label: 'Semana 3' },
      { value: 'SEMANA 4', label: 'Semana 4' },
    ];
  }
  return [];
}

function toUpperOrSame(v) {
  if (!v) return v;
  return (typeof v === 'string') ? v.trim().toUpperCase() : v;
}

function normalizeDiaForDisplay(dia) {
  // Acepta 'Lunes' o 'LUNES' y devuelve el label correcto para UI
  const up = toUpperOrSame(dia);
  const found = DIAS.find(d => d.value === up);
  return found ? found.label : dia || '';
}

function VisitChangesModal({ isOpen, onClose, clientes, draft, setDraft, onSave }) {
  const changesByClient = draft?.changesByClient || {};
  const notas = draft?.notas || '';

  // UI state
  const [notasPromptOpen, setNotasPromptOpen] = useState(false);
  const [tempNotas, setTempNotas] = useState(notas || '');
  // Filtros
  const [searchText, setSearchText] = useState('');
  const [filterDiaActual, setFilterDiaActual] = useState(null);
  const [filterFreqActual, setFilterFreqActual] = useState(null);
  const [onlyChanges, setOnlyChanges] = useState(false);
  // Ordenación
  const [sortBy, setSortBy] = useState(null); // 'codigo' | 'cliente' | 'estado'
  const [sortDir, setSortDir] = useState('asc');

  const handleNotasChange = (e) => {
    setDraft(prev => ({ ...prev, notas: e.target.value }));
  };

  const handleChange = (clt_codigo, field, value) => {
    const newVal = value;
    setDraft(prev => {
      const current = prev.changesByClient?.[clt_codigo] || {};
      const updated = { ...current, [field]: newVal };

      // Si frecuencia cambia a SEMANAL, limpiar semana
      if (field === 'frecuencia' && newVal === 'SEMANAL') {
        delete updated.semana;
      }
      // Si se borra frecuencia (no debería en UI), limpiar semana
      if (field === 'frecuencia' && !newVal) {
        delete updated.semana;
      }

      const updatedMap = { ...(prev.changesByClient || {}), [clt_codigo]: updated };
      return { ...prev, changesByClient: updatedMap };
    });
  };

  const resumenActual = useMemo(() => {
    const dias = {};
    const frecs = { SEMANAL: 0, QUINCENAL: 0, MENSUAL: 0 };
    const semanas = {};

    (clientes || []).forEach(c => {
      const diaUp = toUpperOrSame(c.clt_diavisita);
      if (diaUp) dias[diaUp] = (dias[diaUp] || 0) + 1;
      if (c.clt_frecuencia) frecs[toUpperOrSame(c.clt_frecuencia)] = (frecs[toUpperOrSame(c.clt_frecuencia)] || 0) + 1;
      if (c.clt_semanavisita) semanas[c.clt_semanavisita] = (semanas[c.clt_semanavisita] || 0) + 1;
    });
    return { dias, frecs, semanas };
  }, [clientes]);

  const resumenProyectado = useMemo(() => {
    const dias = {};
    const frecs = { SEMANAL: 0, QUINCENAL: 0, MENSUAL: 0 };
    const semanas = {};

    (clientes || []).forEach(c => {
      const ch = changesByClient[c.clt_codigo] || {};
      const dia = toUpperOrSame(ch.dia) || toUpperOrSame(c.clt_diavisita);
      const freq = toUpperOrSame(ch.frecuencia) || toUpperOrSame(c.clt_frecuencia);
      let semana = ch.semana !== undefined ? ch.semana : c.clt_semanavisita;

      if (dia) dias[dia] = (dias[dia] || 0) + 1;
      if (freq) frecs[freq] = (frecs[freq] || 0) + 1;

      // Si la frecuencia es SEMANAL, semana debe ser vacío
      if (freq === 'SEMANAL') semana = '';
      if (semana) semanas[semana] = (semanas[semana] || 0) + 1;
    });

    return { dias, frecs, semanas };
  }, [clientes, changesByClient]);

  // Datos ordenados y flag de cambio para Resumen proyectado
  const resumenProyectadoUI = useMemo(() => {
    const makeEntries = (order, proj, actual) => {
      return order
        .filter(k => (proj[k] || 0) > 0 || (actual[k] || 0) > 0)
        .map(k => ({ key: k, value: proj[k] || 0, changed: (proj[k] || 0) !== (actual[k] || 0) }));
    };
    return {
      frecs: makeEntries(ORDER_FRECS, resumenProyectado.frecs || {}, resumenActual.frecs || {}),
      dias: makeEntries(ORDER_DIAS, resumenProyectado.dias || {}, resumenActual.dias || {}),
      semanas: makeEntries(ORDER_SEMANAS, resumenProyectado.semanas || {}, resumenActual.semanas || {}),
    };
  }, [resumenProyectado, resumenActual]);

  const filas = useMemo(() => {
    return (clientes || []).map(c => {
      const ch = changesByClient[c.clt_codigo] || {};
      const diaNuevo = ch.dia || toUpperOrSame(c.clt_diavisita);
      const frecuenciaNueva = ch.frecuencia || toUpperOrSame(c.clt_frecuencia);
      const semanaNueva = ch.semana !== undefined ? ch.semana : c.clt_semanavisita;

      const changed = (
        (diaNuevo && toUpperOrSame(c.clt_diavisita) !== diaNuevo) ||
        (frecuenciaNueva && toUpperOrSame(c.clt_frecuencia) !== frecuenciaNueva) ||
        ((frecuenciaNueva !== 'SEMANAL') && (semanaNueva || '') !== (c.clt_semanavisita || ''))
      );

      // Validación por fila
      let error = null;
      if (frecuenciaNueva === 'QUINCENAL' && !(['SEMANA 1-3','SEMANA 2-4'].includes(semanaNueva))) {
        error = 'Selecciona semana 1-3 o 2-4';
      }
      if (frecuenciaNueva === 'MENSUAL' && !(['SEMANA 1','SEMANA 2','SEMANA 3','SEMANA 4'].includes(semanaNueva))) {
        error = 'Selecciona una semana 1..4';
      }

      return {
        c,
        diaActual: toUpperOrSame(c.clt_diavisita),
        frecuenciaActual: toUpperOrSame(c.clt_frecuencia),
        semanaActual: c.clt_semanavisita || '',
        diaNuevo,
        frecuenciaNueva,
        semanaNueva: frecuenciaNueva === 'SEMANAL' ? '' : (semanaNueva || ''),
        changed,
        error,
      };
    });
  }, [clientes, changesByClient]);

  const totalCambios = useMemo(() => filas.filter(f => f.changed).length, [filas]);
  const hayErrores = useMemo(() => filas.some(f => f.error && f.changed), [filas]);

  const filasFiltradasOrdenadas = useMemo(() => {
    let arr = filas;
    // Filtros
    if (searchText && searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      arr = arr.filter(f =>
        String(f.c.clt_codigo).toLowerCase().includes(q) ||
        (String(f.c.clt_nombre || '')).toLowerCase().includes(q) ||
        (String(f.c.clt_ciudad || '')).toLowerCase().includes(q) ||
        (String(f.c.clt_zonadespacho || '')).toLowerCase().includes(q)
      );
    }
    if (filterDiaActual) {
      arr = arr.filter(f => f.diaActual === filterDiaActual);
    }
    if (filterFreqActual) {
      arr = arr.filter(f => f.frecuenciaActual === filterFreqActual);
    }
    if (onlyChanges) {
      arr = arr.filter(f => f.changed);
    }
    // Ordenación
    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        if (sortBy === 'codigo') {
          return String(a.c.clt_codigo).localeCompare(String(b.c.clt_codigo)) * dir;
        }
        if (sortBy === 'cliente') {
          return String(a.c.clt_nombre || '').localeCompare(String(b.c.clt_nombre || '')) * dir;
        }
        if (sortBy === 'estado') {
          const va = a.changed ? 1 : 0;
          const vb = b.changed ? 1 : 0;
          if (va !== vb) return (va - vb) * dir;
          return String(a.c.clt_codigo).localeCompare(String(b.c.clt_codigo)) * dir;
        }
        if (sortBy === 'ciudad') {
          return String(a.c.clt_ciudad || '').localeCompare(String(b.c.clt_ciudad || '')) * dir;
        }
        if (sortBy === 'zonadespacho') {
          return String(a.c.clt_zonadespacho || '').localeCompare(String(b.c.clt_zonadespacho || '')) * dir;
        }
        return 0;
      });
    }
    return arr;
  }, [filas, searchText, filterDiaActual, filterFreqActual, onlyChanges, sortBy, sortDir]);

  const handleGuardar = async () => {
    await performGuardar(notas);
  };

  const performGuardar = async (notesValue) => {
    if (totalCambios === 0) {
      toast.info('No hay cambios para guardar');
      return;
    }
    if (hayErrores) {
      toast.error('Corrige los errores antes de guardar');
      return;
    }

    const detalles = filas
      .filter(f => f.changed)
      .map(f => ({
        clt_codigo: f.c.clt_codigo,
        dia_nuevo: f.diaNuevo,
        frecuencia_nueva: f.frecuenciaNueva,
        semana_nueva: f.frecuenciaNueva === 'SEMANAL' ? null : (f.semanaNueva || null),
        dia_actual: f.diaActual,
        frecuencia_actual: f.frecuenciaActual,
        semana_actual: f.semanaActual || null,
      }));

    try {
      await onSave(detalles, notesValue);
    } catch (e) {
      // onSave maneja toasts/errores en App
    }
  };

  const abrirNotas = () => {
    setTempNotas(notas || '');
    setNotasPromptOpen(true);
  };
  const confirmarGuardar = async () => {
    await performGuardar(tempNotas);
    setNotasPromptOpen(false);
  };
  const guardarSinNotas = async () => {
    await performGuardar('');
    setNotasPromptOpen(false);
  };

  const toggleSort = (key) => {
    setSortBy(prev => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  };

  const getSemanaOptions = (freq) => semanaOptionsByFrecuencia(freq);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Gestión de Cambios de Visitas</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          

          <SummaryRow>
            <SummaryCol>
              <div>
                <SectionTitle>Resumen actual</SectionTitle>
                <Grid>
                  <Card>
                    <CardTitle>Total clientes</CardTitle>
                    <CardValue>{clientes?.length || 0}</CardValue>
                  </Card>

                  <Card>
                    <CardTitle>Días</CardTitle>
                    <ChipList>
                      {ORDER_DIAS.map(k => {
                        const v = (resumenActual.dias || {})[k] || 0;
                        return v > 0 ? (
                          <Chip key={`act-dia-${k}`}>{normalizeDiaForDisplay(k)} <Qty>{v}</Qty></Chip>
                        ) : null;
                      })}
                    </ChipList>
                  </Card>

                  <Card>
                    <CardTitle>Frecuencias y semanas</CardTitle>

                    <SubHeader>Semanal</SubHeader>
                    <ChipList>
                      {((resumenActual.frecs || {}).SEMANAL || 0) > 0 && (
                        <Chip key="act-freq-semanal">Semanal <Qty>{(resumenActual.frecs || {}).SEMANAL}</Qty></Chip>
                      )}
                    </ChipList>

                    <SubHeader>Quincenal (Semanas) <Qty>{(resumenActual.frecs || {}).QUINCENAL || 0}</Qty></SubHeader>
                    <ChipList>
                      {['SEMANA 1-3','SEMANA 2-4'].map(k => {
                        const v = (resumenActual.semanas || {})[k] || 0;
                        return v > 0 ? (
                          <Chip key={`act-semana-${k}`}>{k.replace('SEMANA ','Semana ')} <Qty>{v}</Qty></Chip>
                        ) : null;
                      })}
                    </ChipList>

                    <SubHeader>Mensual <Qty>{(resumenActual.frecs || {}).MENSUAL || 0}</Qty></SubHeader>
                    <ChipList>
                      {((resumenActual.frecs || {}).MENSUAL || 0) > 0 && (
                        <Chip key="act-freq-mensual">Mensual <Qty>{(resumenActual.frecs || {}).MENSUAL}</Qty></Chip>
                      )}
                    </ChipList>
                    <ChipList>
                      {['SEMANA 1','SEMANA 2','SEMANA 3','SEMANA 4'].map(k => {
                        const v = (resumenActual.semanas || {})[k] || 0;
                        return v > 0 ? (
                          <Chip key={`act-semana-m-${k}`}>{k.replace('SEMANA ','S')} <Qty>{v}</Qty></Chip>
                        ) : null;
                      })}
                    </ChipList>
                  </Card>
                </Grid>
              </div>
            </SummaryCol>

            <SummaryCol>
              <div>
                <SectionTitle>Resumen proyectado</SectionTitle>
                <Grid>
                  <Card>
                    <CardTitle>Total clientes</CardTitle>
                    <CardValue>{clientes?.length || 0}</CardValue>
                  </Card>

                  <Card>
                    <CardTitle>Días</CardTitle>
                    <ChipList>
                      {ORDER_DIAS.map(k => {
                        const item = (resumenProyectadoUI.dias || []).find(x => x.key === k);
                        if (!item || item.value <= 0) return null;
                        return (
                          <Chip key={`proj-dia-${k}`} $changed={item.changed}>
                            {normalizeDiaForDisplay(k)} <Qty>{item.value}</Qty>
                          </Chip>
                        );
                      })}
                    </ChipList>
                  </Card>

                  <Card>
                    <CardTitle>Frecuencias y semanas</CardTitle>

                    <SubHeader>Semanal</SubHeader>
                    <ChipList>
                      {(() => {
                        const it = (resumenProyectadoUI.frecs || []).find(x => x.key === 'SEMANAL');
                        return it && it.value > 0 ? (
                          <Chip key="proj-freq-semanal" $changed={it.changed}>Semanal <Qty>{it.value}</Qty></Chip>
                        ) : null;
                      })()}
                    </ChipList>

                    <SubHeader>Quincenal (Semanas) <Qty>{(() => { const it = (resumenProyectadoUI.frecs || []).find(x => x.key === 'QUINCENAL'); return it ? it.value : 0; })()}</Qty></SubHeader>
                    <ChipList>
                      {['SEMANA 1-3','SEMANA 2-4'].map(k => {
                        const it = (resumenProyectadoUI.semanas || []).find(x => x.key === k);
                        return it && it.value > 0 ? (
                          <Chip key={`proj-semana-${k}`} $changed={it.changed}>{k.replace('SEMANA ','Semana ')} <Qty>{it.value}</Qty></Chip>
                        ) : null;
                      })}
                    </ChipList>

                    <SubHeader>Mensual <Qty>{(() => { const it = (resumenProyectadoUI.frecs || []).find(x => x.key === 'MENSUAL'); return it ? it.value : 0; })()}</Qty></SubHeader>
                    <ChipList>
                      {(() => {
                        const it = (resumenProyectadoUI.frecs || []).find(x => x.key === 'MENSUAL');
                        return it && it.value > 0 ? (
                          <Chip key="proj-freq-mensual" $changed={it.changed}>Mensual <Qty>{it.value}</Qty></Chip>
                        ) : null;
                      })()}
                    </ChipList>
                    <ChipList>
                      {['SEMANA 1','SEMANA 2','SEMANA 3','SEMANA 4'].map(k => {
                        const it = (resumenProyectadoUI.semanas || []).find(x => x.key === k);
                        return it && it.value > 0 ? (
                          <Chip key={`proj-semana-m-${k}`} $changed={it.changed}>{k.replace('SEMANA ','S')} <Qty>{it.value}</Qty></Chip>
                        ) : null;
                      })}
                    </ChipList>
                  </Card>
                </Grid>
              </div>
            </SummaryCol>
          </SummaryRow>

          <div>
            <SectionTitle>Detalle de cambios</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Buscar por código o nombre"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ced4da', borderRadius: 6, minWidth: 220 }}
              />
              <Select
                styles={selectStyles}
                options={[{ value: null, label: 'Todos los días' }, ...DIAS]}
                value={[{ value: null, label: 'Todos los días' }, ...DIAS].find(o => o.value === filterDiaActual) || null}
                onChange={(opt) => setFilterDiaActual(opt?.value ?? null)}
                isClearable
                menuPortalTarget={document.body}
                placeholder="Día actual"
              />
              <Select
                styles={selectStyles}
                options={[{ value: null, label: 'Todas las frecuencias' }, ...FRECUENCIAS]}
                value={[{ value: null, label: 'Todas las frecuencias' }, ...FRECUENCIAS].find(o => o.value === filterFreqActual) || null}
                onChange={(opt) => setFilterFreqActual(opt?.value ?? null)}
                isClearable
                menuPortalTarget={document.body}
                placeholder="Frecuencia actual"
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={onlyChanges}
                  onChange={(e) => setOnlyChanges(e.target.checked)}
                />
                Solo cambios
              </label>
            </div>
            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort('codigo')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Código {sortBy === 'codigo' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </Th>
                  <Th onClick={() => toggleSort('cliente')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Cliente {sortBy === 'cliente' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </Th>
                  <Th onClick={() => toggleSort('ciudad')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Ciudad {sortBy === 'ciudad' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </Th>
                  <Th onClick={() => toggleSort('zonadespacho')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Zona Despacho {sortBy === 'zonadespacho' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </Th>
                  <Th>Actual</Th>
                  <Th>Nuevos</Th>
                  <Th onClick={() => toggleSort('estado')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Estado {sortBy === 'estado' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {filasFiltradasOrdenadas.map((f) => {
                  const TrComp = f.changed ? RowChanged : 'tr';
                  return (
                    <TrComp key={f.c.clt_codigo}>
                      <Td>{f.c.clt_codigo}</Td>
                      <Td>{f.c.clt_nombre}</Td>
                      <Td>{f.c.clt_ciudad || '-'}</Td>
                      <Td>{f.c.clt_zonadespacho || '-'}</Td>
                      <Td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span>Día: {normalizeDiaForDisplay(f.diaActual)}</span>
                          <span>Frecuencia: {f.frecuenciaActual}</span>
                          {f.semanaActual ? <span>Semana: {f.semanaActual}</span> : null}
                        </div>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <Select
                            styles={selectStyles}
                            options={DIAS}
                            value={DIAS.find(o => o.value === f.diaNuevo) || null}
                            onChange={(opt) => handleChange(f.c.clt_codigo, 'dia', opt?.value || null)}
                            menuPortalTarget={document.body}
                            placeholder="Día"
                          />
                          <Select
                            styles={selectStyles}
                            options={FRECUENCIAS}
                            value={FRECUENCIAS.find(o => o.value === f.frecuenciaNueva) || null}
                            onChange={(opt) => handleChange(f.c.clt_codigo, 'frecuencia', opt?.value || null)}
                            menuPortalTarget={document.body}
                            placeholder="Frecuencia"
                          />
                          {f.frecuenciaNueva !== 'SEMANAL' && (
                            <Select
                              styles={selectStyles}
                              options={getSemanaOptions(f.frecuenciaNueva)}
                              value={getSemanaOptions(f.frecuenciaNueva).find(o => o.value === f.semanaNueva) || null}
                              onChange={(opt) => handleChange(f.c.clt_codigo, 'semana', opt?.value || null)}
                              menuPortalTarget={document.body}
                              placeholder="Semana"
                            />
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <DiffBadge $changed={f.changed}>{f.changed ? 'Cambio' : 'Sin cambio'}</DiffBadge>
                          {f.error && f.changed && (
                            <span style={{ color: '#dc3545', fontSize: '0.8rem' }}>{f.error}</span>
                          )}
                        </div>
                      </Td>
                    </TrComp>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <Footer>
          <Left>
            <SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>
            <DangerButton onClick={() => setDraft({ changesByClient: {}, notas: '' })}>Descartar borrador</DangerButton>
          </Left>
          <Right>
            <RequireRole 
              roles={['admin','gerente', 'supervisor']}
              fallback={
                <span style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '0.9rem' }}>
                  No tienes permisos para modificar visitas
                </span>
              }
            >
              {notasPromptOpen ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <NotesArea
                    value={tempNotas}
                    onChange={(e) => setTempNotas(e.target.value)}
                    placeholder="Notas (opcional)"
                    style={{ minWidth: 280, minHeight: 44 }}
                  />
                  <PrimaryButton onClick={confirmarGuardar} disabled={totalCambios === 0 || hayErrores}>
                    Confirmar guardado
                  </PrimaryButton>
                  <SecondaryButton onClick={guardarSinNotas} disabled={totalCambios === 0 || hayErrores}>
                    Guardar sin notas
                  </SecondaryButton>
                  <SecondaryButton onClick={() => setNotasPromptOpen(false)}>Cancelar</SecondaryButton>
                </div>
              ) : (
                <PrimaryButton onClick={abrirNotas} disabled={totalCambios === 0 || hayErrores}>
                  {totalCambios > 0 ? `Guardar cambios (${totalCambios})` : 'Guardar cambios'}
                </PrimaryButton>
              )}
            </RequireRole>
          </Right>
        </Footer>
      </ModalContent>
    </ModalOverlay>
  );
}

export default VisitChangesModal;
